import { useMutation, useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { chatApi } from "./api";
import type { InboxRequest } from "./types";

/** Centralised query-key factory — keeps cache keys consistent across the feature */
export const chatKeys = {
  all: ["chat"] as const,
  inbox: (request: InboxRequest) =>
    [...chatKeys.all, "inbox", request] as const,
  messages: (chatroomId: string) =>
    [...chatKeys.all, "messages", chatroomId] as const,
  participants: (chatroomId: string) =>
    [...chatKeys.all, "participants", chatroomId] as const,
};

// ── Inbox ──────────────────────────────────────────────────────────────────

export const useInbox = (request: InboxRequest, enabled = true) =>
  useQuery({
    queryKey: chatKeys.inbox(request),
    queryFn: () => chatApi.getInbox(request),
    enabled,
  });

// ── Chatroom Messages ──────────────────────────────────────────────────────

export const useChatroomMessages = (chatroomId: string, enabled = true) =>
  useQuery({
    queryKey: chatKeys.messages(chatroomId),
    queryFn: () => chatApi.getChatroomMessages(chatroomId),
    enabled: enabled && !!chatroomId,
    staleTime: 0, // Always refetch when switching conversations
  });

// ── Chatroom Participants ──────────────────────────────────────────────────

export const useChatroomParticipants = (chatroomId: string, enabled = true) =>
  useQuery({
    queryKey: chatKeys.participants(chatroomId),
    queryFn: () => chatApi.getChatroomParticipants(chatroomId),
    enabled: enabled && !!chatroomId,
  });

// ── Update Message Status (mark as read) ──────────────────────────────────

export const useUpdateMessageStatus = () =>
  useMutation({
    mutationFn: (chatRoomId: string) => chatApi.updateMessageStatus(chatRoomId),
  });

// ── Pin / Unpin Chatroom ──────────────────────────────────────────────

export const usePinChat = () =>
  useMutation({
    mutationFn: (payload: import('./types').PinChatRequest) => chatApi.pinChat(payload),
  });

// ── Leave Conversation ──────────────────────────────────────────────

export const useLeaveConversation = () =>
  useMutation({
    mutationFn: (payload: import('./types').LeaveConversationRequest) =>
      chatApi.leaveConversation(payload),
  });

// ── Rename Chatroom ──────────────────────────────────────────────

export const useRenameChatroom = () =>
  useMutation({
    mutationFn: (payload: import('./types').RenameChatroomRequest) =>
      chatApi.renameChatroom(payload),
  });
// ── Staff Facilities ──────────────────────────────────────

export const useStaffFacilities = () =>
  useQuery({
    queryKey: [...chatKeys.all, "staff-facilities"] as const,
    queryFn: () => chatApi.getStaffFacilities(),
    staleTime: 5 * 60 * 1000,
  });

// ── Staff Participants ────────────────────────────────────

export const useStaffParticipants = (
  params: { facilityGuid: string; q?: string },
  enabled = true,
) =>
  useInfiniteQuery({
    queryKey: [...chatKeys.all, "staff-participants", params] as const,
    queryFn: ({ pageParam }) =>
      chatApi.getStaffParticipants({
        facilityGuid: params.facilityGuid,
        limit: params.q ? 100 : 20,
        next: pageParam,
        q: params.q,
      }),
    initialPageParam: "",
    getNextPageParam: (lastPage) => lastPage.next || undefined,
    enabled: enabled && !!params.facilityGuid,
  });

// ── Validate Chat Participant ────────────────────────────────────

export const useValidateChatParticipant = () =>
  useMutation({
    mutationFn: (payload: import('./types').ValidateParticipantRequest) =>
      chatApi.validateChatParticipant(payload),
  });

// ── Create Chatroom ─────────────────────────────────────────

export const useCreateChatroom = () =>
  useMutation({
    mutationFn: (payload: import('./types').CreateChatroomRequest) =>
      chatApi.createChatroom(payload),
  });
// ── Add Chatroom Participant ──────────────────────────────────────────────

export const useAddChatroomParticipant = () =>
  useMutation({
    mutationFn: (payload: import('./types').AddParticipantRequest) =>
      chatApi.addChatroomParticipant(payload),
  });